<template>
  <div
    :class="[
      'border-contrast--full relative rounded-xl md:h-full md:rounded-none md:!border-0',
      {
        'overflow-hidden': !isInMinimalWidgetLayout,
        'overflow-y-auto': isInMinimalWidgetLayout,
        'widget-scheme-card': isInWidget,
        'md:hidden': !inModal,
      },
    ]"
  >
    <PortalTarget multiple name="order-form" class="z-[100]" />
    <PortalTarget name="order-form-modals-mobile" />
    <div
      v-if="invalidTickets.length || invalidSupplementaryServices.length"
      class="absolute z-[55] size-full"
    >
      <PortalTarget name="order-form-invalid-tickets" class="z-[100]" />
    </div>
    <UiCard
      class="border-contrast--full overflow-hidden !rounded-b-none !border-x-0 !border-t-0 py-5 md:!py-4"
      :class="{
        '!border-b-0': isEventPast,
        'py-4 pr-10': isMobileWidget,
      }"
    >
      <UiContent>
        <EventOrderInfo v-if="event" :value="event" />
      </UiContent>
      <ClientOnly>
        <div
          v-if="eventPageKitLoading"
          class="absolute left-0 top-0 z-[1] size-full bg-white/75"
        />
      </ClientOnly>
    </UiCard>
    <section
      v-if="!isEventPast"
      :class="[
        'relative overflow-x-hidden md:h-[calc(100%_-_73px)] md:overflow-y-scroll md:bg-background xs:h-[calc(100%_-_64px)]',
        {
          'md:pt-5': !isInMinimalWidgetLayout,
          'pt-0': isInMinimalWidgetLayout,
          'md:h-[calc(100%_-_193px)] xs:h-[calc(100%_-_169px)]':
            tickets.length > 0,
          'md:h-[calc(100%_-_159px)] xs:h-[calc(100%_-_150px)]': isMobileWidget,
        },
      ]"
    >
      <section
        v-if="!isOrderKitLoaded || (isOrderKitLoaded && isTicketsAvailable)"
        class="relative min-h-[476px] md:h-[calc(100%_-_73px)] md:min-h-1 xs:h-[calc(100%_-_64px)]"
        :class="{
          'md:!h-[calc(100%_-_193px)] xs:!h-[calc(100%_-_169px)]':
            tickets.length > 0,
          'md:h-[calc(100%_-_159px)] xs:h-[calc(100%_-_150px)]': isMobileWidget,
        }"
      >
        <PortalTarget name="scheme-wrapper" />
        <LazyGlobalSchemeRenderer
          v-if="isOrderKitLoaded && showScheme"
          v-model="tickets"
          :reset-scheme="resetScheme"
          :save-sector-on-reset="saveSectorOnReset"
        />
        <GlobalSchemeLoader v-else class="absolute left-0 top-0 md:hidden" />
      </section>

      <section v-if="isOrderKitLoaded && !isTicketsAvailable">
        <div class="flex min-h-[516px] items-center justify-center">
          <div v-if="isSchemeError" class="space-y-4">
            <p>
              К сожалению, произошла ошибка при загрузке схемы. <br />
              Попробуйте загрузить схему снова.
            </p>
            <UiButton
              v-ui-test-name="'schemeCardBuyButton'"
              class="buy-button self-center md:w-full md:!py-3 md:text-sm"
              primary
              @click="renderScheme"
            >
              Загрузить
            </UiButton>
          </div>
          <p v-else-if="isUniqueEvent">
            К сожалению, билеты закончились. <br />
            Попробуйте выбрать другое мероприятие.
          </p>
          <p v-else>
            К сожалению, билеты на эту дату закончились. <br />
            Пожалуйста, выберите другую дату или мероприятие.
          </p>
        </div>
      </section>

      <ClientOnly>
        <div
          v-if="showScheme && isTicketsAvailable && isOrderKitLoaded"
          :class="[
            'border-contrast--full bottom-0 left-0 z-40 flex min-h-20 w-full rounded-b-xl !border-x-0 !border-b-0 bg-white pr-5',
            'md:z-10 md:block md:min-h-0 md:rounded-none md:px-4 md:!pb-4 md:pt-3',
            'sm:h-auto',
            {
              'relative md:fixed': !isInMinimalWidgetLayout,
              fixed: isInMinimalWidgetLayout,
              'items-center': !hasPodeliPaymentMethod,
            },
          ]"
        >
          <TicketsSummary
            v-if="tickets.length"
            :count="tickets.length"
            :price="ticketsPriceInitial ?? 0"
            :loading="orderIsLoading"
            class="hidden md:mb-2.5 md:block"
          />
          <SelectedTicketsList
            :grouped-tickets="getGroupedTicketsList(tickets)"
            class="md:mb-2.5"
          />
          <div class="my-2 flex flex-col justify-center gap-2">
            <div class="flex items-center">
              <TicketsSummary
                v-if="tickets.length"
                :count="tickets.length"
                :price="ticketsPriceInitial ?? 0"
                :loading="orderIsLoading"
                class="md:mb-2.5 md:hidden"
              />
              <AnalyticsEventWrapper
                v-slot="{ analyticsHandler }"
                event="checkout_button"
                yandex-metrika
                event-data="short"
                :event-value="{
                  placement: 'Выбор билетов',
                  stoimost: ticketsPriceInitial,
                  quantity_tickets: tickets.length,
                  min_price: activity?.priceRange?.min,
                  max_price: activity?.priceRange?.max,
                  date_from: activity?.dateRange?.beginsAt,
                  date_to: activity?.dateRange?.endsAt,
                }"
                class="ml-6 flex items-center md:ml-0 md:w-full md:flex-col"
              >
                <UiButton
                  v-ui-test-name="'schemeCardBuyButton'"
                  :class="{
                    '!cursor-not-allowed !opacity-30': isButtonDisabled,
                  }"
                  class="buy-button self-center md:w-full md:!py-3 md:text-sm"
                  primary
                  @click="onBuyButtonClick(), analyticsHandler()"
                >
                  Купить
                </UiButton>
              </AnalyticsEventWrapper>
            </div>
            <PodeliButton
              v-if="
                hasPodeliPaymentMethod &&
                tickets.length > 0 &&
                ticketsPriceInitial
              "
              :value="ticketsPriceInitial"
              @click="showPodeliPopup()"
            />
          </div>
        </div>
      </ClientOnly>
    </section>
    <div v-else class="flex min-h-[516px] items-center justify-center">
      <p v-if="isUniqueEvent">
        К сожалению, билеты закончились. <br />
        Попробуйте выбрать другое мероприятие.
      </p>
      <p v-else>
        К сожалению, билеты на эту дату закончились. <br />
        Пожалуйста, выберите другую дату или мероприятие.
      </p>
    </div>
    <PodeliWidget
      v-if="!isOrderFormShown"
      id="podeli-utility-widget"
      class="max-h-0 overflow-hidden"
      :value="1000"
    />
  </div>
</template>

<script lang="ts" setup>
import debounce from 'lodash/debounce.js'
import differenceWith from 'lodash/differenceWith.js'
import isEqual from 'lodash/isEqual.js'
import type { AnalyticEvents, WidgetOrderPayload } from '@/models'

const props = defineProps({
  widgetOrderPayload: {
    type: Object as PropType<WidgetOrderPayload>,
    required: false,
    default: () => ({}),
  },
  isMobileVersion: {
    type: Boolean,
    default: false,
  },
  inModal: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const showPodeliPopup = () => {
  const buttons = document.querySelectorAll('#podeli-utility-widget button')
  const element = buttons[buttons.length - 1]

  if (element instanceof HTMLElement) {
    element.click()
  }
}

const { $analytics } = useNuxtApp()
const { isOrderKitLoaded } = storeToRefs(useOrderKitStore())
const {
  paymentMethodId,
  certificates,
  isInsured,
  loading: orderIsLoading,
  hasPodeliPaymentMethod,
} = storeToRefs(useOrderStore())

const { isAllQuotasFilled } = storeToRefs(useQuotaStore())
const { tickets, ticketsPriceInitial, invalidTickets } = storeToRefs(
  useTicketStore(),
)
const { getGroupedTicketsList, deleteInvalidTickets } = useTicketStore()
const { calculateOrder } = useOrderStore()

const { calculateGroupOrder } = useGroupOrderStore()
const { isGroupPurchaseSelected } = storeToRefs(useGroupOrderStore())
const {
  addToCart: flocktoryAddToCart,
  removeFromCart: flocktoryRemoveFromCart,
} = useFlocktory()

const { isAuthorized } = storeToRefs(useAuthStore())

const { eventDateSelectors } = storeToRefs(useEventDateSelector())
const { isEventPast, event } = storeToRefs(useEventStore())
const { setEventChangedHandler } = useEventPageKitStore()
const {
  loading: eventPageKitLoading,
  resetScheme,
  saveSectorOnReset,
} = storeToRefs(useEventPageKitStore())

const { getOrderKit, reloadOrderKit } = useOrderKitStore()

const { isTicketsAvailable } = storeToRefs(useQuotaStore())
const { isJsonSchemeType } = storeToRefs(useGlobalSchemeStore())
const { showModal, closeModal } = useModalStore()
const { createNotification } = useNotificationLayerStore()
const {
  createAnalyticsEvent,
  createAnalyticsEcommerceEvent,
  getEcommerceTickets,
} = useAnalyticsStore()
const { isMdAndDown } = useBreakpoints()

const isUniqueEvent = computed(() => eventDateSelectors.value?.length === 0)
const { activity } = storeToRefs(useActivityStore())

const isMobileWidget = computed(() => props.isMobileVersion && isWidget)

const orderFormContainer = defineAsyncComponent(
  () => import('~/components/entities/Order/OrderFormContainer.client.vue'),
)

const { payAttentionToWarnings } = useWarning()
const isCartAnalyticsSended = ref(false)
/**
 * флаг показывает, что заказ уже был начат
 * и не дает триггериться различным событиям,
 * если все билеты были удалены и добавлены заново
 */
const isOrderStarted = ref<boolean>(false)

const isOrderFormShown = ref(false)

const isButtonDisabled = computed(
  () =>
    !tickets.value.length ||
    !isAllQuotasFilled.value ||
    hasInvalidTariffDependencies.value,
)

const onBuyButtonClick = (openTicketsList?: boolean) => {
  if (!isAllQuotasFilled.value && !isJsonSchemeType.value) {
    showQuotaFillingNotification()

    return
  }

  if (isButtonDisabled.value) {
    payAttentionToWarnings()

    return
  }

  if (tickets.value.length === 0) {
    return
  }

  const isMobileOrTablet = isMdAndDown(window.innerWidth)

  if (!isMobileOrTablet) {
    closeModal('closeAll')
    scrollToOrderForm()
  }
  closeModal('ticketToEntranceSectorModal')
  closeModal('ticketToSeatSectorModal')

  if (isInMinimalWidgetLayout) {
    window.document.children[0].scrollTo(0, 0)
    window.document.body.style.overflow = 'hidden'
  }
  showModal({
    key: 'orderModal',
    modalConfig: {
      component: orderFormContainer,
      isModalCloseButtonShown: true,
      modalSize:
        isMobileOrTablet || isInMinimalWidgetLayout
          ? 'fullScreen'
          : 'vertical-right',
      modalTeleportTarget: isInMinimalWidgetLayout ? 'default' : 'order-form',
      disableClosingOnOverlay: !isMobileOrTablet,
      modalComponentProps: {
        openTicketsList,
        widgetOrderPayload: props.widgetOrderPayload,
        isCartAnalyticsSended,
      },
      modalTransition: isInMinimalWidgetLayout ? 'fade' : 'slide-right',
      lockScroll: true,
      onModalClosed: () => {
        window.document.body.style.removeProperty('overflow')
        isOrderFormShown.value = false
      },
      onAfterShow: () => {
        isOrderFormShown.value = true
      },
      emits: {
        'send-сart-analytics': (value: boolean): void => {
          isCartAnalyticsSended.value = value
        },
      },
    },
  })

  createAnalyticsEcommerceEvent({
    event: 'begin_checkout',
    value: getEcommerceTickets(tickets.value),
  })
}
/**
 * скролл к форме заказа, если верх формы находится вне viewport
 */

const scrollToOrderForm = () => {
  if (!window) return

  const document = window.document
  const orderFormElement = document.querySelector('#order-form')

  let isOrderFormInViewport = false

  if (orderFormElement) {
    const rect = orderFormElement.getBoundingClientRect()

    isOrderFormInViewport = rect.top >= 0
  }
  if (!isOrderFormInViewport) scrollToElement('#order-form')
}

watch(tickets, (value, oldValue) => {
  isCartAnalyticsSended.value = false
  !value.length && closeModal('orderModal')

  if (value.length) {
    isOrderStarted.value = true
  }

  const createAnalyticsEvents = (
    ecommerceEvent: AnalyticEvents,
    analitycsEvent: AnalyticEvents,
    items: any[],
  ) => {
    if (items.length) {
      createAnalyticsEcommerceEvent({
        event: ecommerceEvent,
        value: items,
      })

      createAnalyticsEvent({
        event: analitycsEvent,
        value: {
          placement: 'Выбор билетов',
          stoimost: ticketsPriceInitial.value,
        },
        isRequiredValuesNeed: true,
        eventData: 'full',
        yandexMetrika: true,
      })
    }
  }

  if (value.length > oldValue.length) {
    const ticketsDifference = differenceWith(value, oldValue, isEqual)
    const ecommerseItems = getEcommerceTickets(ticketsDifference)

    flocktoryAddToCart(
      ticketsDifference[ticketsDifference.length - 1].price,
      event.value,
      activity.value,
    )
    createAnalyticsEvents('add_to_cart', 'add_tickets_to_cart', ecommerseItems)
  } else {
    const ecommerseItems = getEcommerceTickets(
      differenceWith(oldValue, value, isEqual),
    )

    flocktoryRemoveFromCart(event.value)
    createAnalyticsEvents('remove_from_cart', 'delete_ticket', ecommerseItems)
  }

  // Делаем запрос на сервер для пересчета заказа
  if (isGroupPurchaseSelected.value && tickets.value.length > 1) {
    calculateGroupOrderWithDebounce()
  } else {
    calculateWithDebounce()
  }
})

const showQuotaFillingNotification = () => {
  createNotification(
    'Места в этом секторе не продаются по отдельности.<br/>Добавьте необходимое количество мест в заказ.',
    { status: 'warning', position: 'top-center' },
  )
}

const calculateGroupOrderWithDebounce = debounce(calculateGroupOrder, 500)
const calculateWithDebounce = debounce(calculateOrder, 500)
// TODO: перенести в сторы

/**
 * Делаем запрос на сервер для пересчета заказа
 * при изменении билетов/промокода/сертификата/способа оплаты
 */
// TODO: здесь это лежать не должно!
watch(
  () => paymentMethodId.value,
  () => {
    calculateWithDebounce()
  },
  {
    deep: true,
  },
)

watch(
  () => certificates.value,
  (newValue, oldValue) => {
    if (newValue.length !== oldValue.length) {
      calculateWithDebounce()
    }
  },
  {
    deep: true,
  },
)

watch(
  () => isInsured.value,
  () => {
    calculateWithDebounce()
  },
  {
    deep: true,
  },
)

const orderFormInvalidTicketsList = defineAsyncComponent(
  () => import('~/components/entities/Order/OrderFormInvalidTicketsList.vue'),
)

watch(
  () => invalidTickets.value,
  (newValue) => {
    if (newValue.length > 0) {
      closeModal('ticketToEntranceSectorModal')
      showModal({
        key: 'invalidTicketsModal',
        modalConfig: {
          component: orderFormInvalidTicketsList,
          isModalCloseButtonShown: false,
          modalTeleportTarget: 'order-form-invalid-tickets',
          modalSize: 'small',
          disableClosingOnOverlay: true,
          type: 'warning',
          onModalClosed: () => {
            deleteInvalidTickets()
            reloadOrderKit()
          },
        },
      })
    }
  },
  {
    deep: true,
  },
)

watch(isAuthorized, (isAuthorized) => {
  if (!isAuthorized) isGroupPurchaseSelected.value = false
})

onUnmounted(() => {
  isGroupPurchaseSelected.value = false
})

const { getScheme } = useGlobalSchemeStore()
const { isSchemeLoaded, isSchemeAvailable, isSchemeError } = storeToRefs(
  useGlobalSchemeStore(),
)

const showScheme = computed(() => {
  return isSchemeAvailable.value
    ? isSchemeLoaded.value || isSchemeError.value
    : true
})

/**
 * VkPixel
 */
const { vkPixelId } = useLayoutStore()
const { addToCart: pixelAddToCart } = useVkPixel(vkPixelId)

const renderScheme = async () => {
  await getOrderKit(event.value!.id)
  await getScheme(event.value!.id)
}

onMounted(() => {
  const { hash } = useRoute()

  if (hash && !props.inModal) return
  renderScheme()
})
setEventChangedHandler(renderScheme)

watch(tickets, (value, oldValue) => {
  if (value.length > oldValue.length) {
    const ticketsDifference = differenceWith(value, oldValue, isEqual)
    const newTicket = ticketsDifference[ticketsDifference.length - 1]

    newTicket && pixelAddToCart(String(newTicket.price), event.value!.id)
  }
})

/**
 * доп.услуги
 */
const { invalidSupplementaryServices } = storeToRefs(
  useSupplementaryServiceStore(),
)
const {
  deleteInvalidSupplementaryServices,
  removeAllSupplementaryServiceItemsForTicket,
} = useSupplementaryServiceStore()

const InvalidSupplementaryServicesModal = defineAsyncComponent(
  () =>
    import(
      '~/components/entities/SupplementaryService/InvalidSupplementaryServicesModal.vue'
    ),
)

watch(
  () => invalidSupplementaryServices.value,
  (newValue) => {
    if (newValue.length) {
      closeModal('orderFormSupplementaryModal')
      showModal({
        key: 'invalidSupplementaryServices',
        modalConfig: {
          component: InvalidSupplementaryServicesModal,
          isModalCloseButtonShown: false,
          modalTeleportTarget: 'order-form-invalid-tickets',
          modalSize: 'small',
          disableClosingOnOverlay: true,
          type: 'warning',
          onModalClosed: () => {
            deleteInvalidSupplementaryServices()
            reloadOrderKit()
          },
        },
      })
    }
  },
)

watch(tickets, (value, oldValue) => {
  if (value.length < oldValue.length) {
    const ticketsDifference = differenceWith(oldValue, value, isEqual)

    const ticketsKeysToDelete = ticketsDifference.map((ticket) => ticket.key)

    ticketsKeysToDelete.forEach((ticketId) =>
      removeAllSupplementaryServiceItemsForTicket(ticketId!),
    )
  }
})

/**
 * tariff dependencies
 */
const { hasInvalidTariffDependencies } = storeToRefs(useTariffStore())

/**
 * widget
 */
const { isWidget } = useRuntimeConfig().public
const isInWidget = isWidget ? inject('isInWidget') : false

const isInMinimalWidgetLayout = isWidget
  ? inject('isInMinimalWidgetLayout')
  : false

watch(tickets, (value) => {
  if (!isWidget) return

  const createWidgetAnalyticsEvent = () => {
    const { widgetIntegrationsKeys } = useWidgetConfigurationStore()

    if (!widgetIntegrationsKeys) return

    $analytics.widget(widgetIntegrationsKeys).trackYmEvent('order.started')
  }

  if (value.length) {
    createWidgetAnalyticsEvent()
  }
})
</script>

<style scoped>
.widget-scheme-card {
  margin-bottom: 96px;
}

@media screen and (max-width: 768px) {
  .widget-scheme-card {
    margin-bottom: 0;
  }
}
</style>
